import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import api from '../lib/api';
import { messageHandler } from '../services/message-handler.service';
import { Form, FormField, FormLabel, FormInput, Button } from '../components/ui';
import PageHeader from '../components/ui/PageHeader';

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Senha atual é obrigatória'),
    newPassword: z.string().min(6, 'Nova senha deve ter no mínimo 6 caracteres'),
    confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  });

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

function ChangePassword() {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (data: ChangePasswordFormData) => {
      const response = await api.post('/password/change', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      return response.data;
    },
    onSuccess: response => {
      if (response.success) {
        messageHandler.success('Senha alterada com sucesso!');
        navigate('/');
      } else {
        messageHandler.error(response.message);
      }
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const onSubmit = (data: ChangePasswordFormData) => {
    changePasswordMutation.mutate(data);
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader title="Alterar Senha" description="Altere sua senha de acesso ao sistema" />

        <div className="mt-8 max-w-xl">
          <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-6">
              <FormField>
                <FormLabel htmlFor="currentPassword" required>
                  Senha Atual
                </FormLabel>
                <FormInput
                  type="password"
                  {...register('currentPassword')}
                  error={!!errors.currentPassword}
                  validationError={errors.currentPassword?.message}
                />
              </FormField>

              <FormField>
                <FormLabel htmlFor="newPassword" required>
                  Nova Senha
                </FormLabel>
                <FormInput
                  type="password"
                  {...register('newPassword')}
                  error={!!errors.newPassword}
                  validationError={errors.newPassword?.message}
                />
              </FormField>

              <FormField>
                <FormLabel htmlFor="confirmPassword" required>
                  Confirmar Nova Senha
                </FormLabel>
                <FormInput
                  type="password"
                  {...register('confirmPassword')}
                  error={!!errors.confirmPassword}
                  validationError={errors.confirmPassword?.message}
                />
              </FormField>

              <div className="flex items-center justify-between">
                <Button type="submit" isLoading={changePasswordMutation.isPending}>
                  <KeyRound className="h-4 w-4 mr-2" />
                  Alterar Senha
                </Button>

                <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
                  Cancelar
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default ChangePassword;
